import React, { useState, useEffect } from "react";
import {
  Panel,
  PanelType,
  PrimaryButton,
  DefaultButton,
  Checkbox,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import {
  IntegrationTagDto,
  listIntegrationTags,
} from "@/api/integrations";

export interface IntegrationFilterOptions {
  statusFilter: "all" | "active" | "inactive";
  selectedTagIds: number[];
}

const defaultFilters: IntegrationFilterOptions = {
  statusFilter: "all",
  selectedTagIds: [],
};

export interface IntegrationFilterPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  selectedFilters: IntegrationFilterOptions;
  onFiltersChange: (filters: IntegrationFilterOptions) => void;
  onApplyFilters: (filters: IntegrationFilterOptions) => void;
  onClearFilters: () => void;
}

const IntegrationFilterPanel: React.FC<IntegrationFilterPanelProps> = ({
  isOpen,
  onDismiss,
  selectedFilters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const [localFilters, setLocalFilters] =
    useState<IntegrationFilterOptions>(selectedFilters);
  const [tags, setTags] = useState<IntegrationTagDto[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const handleApply = () => {
    onFiltersChange(localFilters);
    onApplyFilters(localFilters);
    onDismiss();
  };

  const handleClear = () => {
    const clearedFilters: IntegrationFilterOptions = { ...defaultFilters };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onClearFilters();
    onApplyFilters(clearedFilters);
    onDismiss();
  };

  const handleStatusChange = (option: "all" | "active" | "inactive") => {
    setLocalFilters((prev) => ({ ...prev, statusFilter: option }));
  };

  const hasActiveFilters = () => {
    return (
      localFilters.statusFilter !== "all" ||
      localFilters.selectedTagIds.length > 0
    );
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedFilters.statusFilter !== "all") count++;
    if (selectedFilters.selectedTagIds.length > 0) count++;
    return count;
  };

  // Load tags when panel opens
  useEffect(() => {
    if (isOpen && tags.length === 0) {
      setLoadingTags(true);
      listIntegrationTags(undefined, 1000)
        .then((data) => setTags(data?.result || []))
        .catch(() => setTags([]))
        .finally(() => setLoadingTags(false));
    }
  }, [isOpen, tags.length]);

  // Sync local filters when prop changes
  useEffect(() => {
    setLocalFilters(selectedFilters);
  }, [selectedFilters]);

  const tagOptions: IDropdownOption[] = tags
    .filter((t) => t.isActive)
    .map((t) => ({ key: t.id, text: t.name }));

  return (
    <Panel
      headerText="Filter Integrations"
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.smallFixedFar}
      closeButtonAriaLabel="Close filter panel"
      isLightDismiss={true}
    >
      <div style={{ padding: "20px 0" }}>
        {/* Status Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>Status</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Checkbox
              label="All"
              checked={localFilters.statusFilter === "all"}
              onChange={() => handleStatusChange("all")}
            />
            <Checkbox
              label="Active"
              checked={localFilters.statusFilter === "active"}
              onChange={() => handleStatusChange("active")}
            />
            <Checkbox
              label="Inactive"
              checked={localFilters.statusFilter === "inactive"}
              onChange={() => handleStatusChange("inactive")}
            />
          </div>
        </div>

        {/* Tags Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>Tags</h4>
          <Dropdown
            placeholder={loadingTags ? "Loading tags..." : "Select tags"}
            options={tagOptions}
            multiSelect
            disabled={loadingTags}
            selectedKeys={localFilters.selectedTagIds}
            onChange={(_, option) => {
              if (option) {
                setLocalFilters((prev) => ({
                  ...prev,
                  selectedTagIds: option.selected
                    ? [...prev.selectedTagIds, option.key as number]
                    : prev.selectedTagIds.filter((k) => k !== option.key),
                }));
              }
            }}
            styles={{ root: { width: "100%" } }}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "30px",
            paddingTop: "20px",
            borderTop: "1px solid #edebe9",
          }}
        >
          <PrimaryButton text="Apply Filters" onClick={handleApply} />
          <DefaultButton
            text="Clear All"
            onClick={handleClear}
            disabled={!hasActiveFilters()}
          />
        </div>

        {getActiveFiltersCount() > 0 && (
          <div
            style={{
              marginTop: "15px",
              fontSize: "12px",
              color: "#605e5c",
            }}
          >
            Active filters: {getActiveFiltersCount()} filter(s) applied
          </div>
        )}
      </div>
    </Panel>
  );
};

export default IntegrationFilterPanel;
